import {useSelector, useDispatch} from 'react-redux';

//SELECTORS
import {
  multiRoomFlowStatusSelector,
  isAltAccoSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';

// COMPONENTS
import ViewRoomDetailsChunk from '../ViewRoomDetails/ViewRoomDetailsChunk';
import Modal, {useModal} from '@stencil/hotels-modal-component';
import RoomFlavors from '../RoomFlavors/RoomFlavors';
import RoomInfoText from '../../common/RoomInfoText';
import {PrimaryLink} from '../../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import ExtraBedRoomTypeInfo from '../ExtraBedRoomTypeInfo/ExtraBedRoomTypeInfo';

// ICONS
import TwoPeopleIcon from '../../../../../../stories/Icons/TwoPeopleIcon';
import HappySingleQuoteIcon from '@stencil/icons/src/HappySingleQuoteIcon/HappySingleQuoteIcon';
import HappyRoomSizeIcon from '@stencil/icons/src/HappyRoomSizeIcon/HappyRoomSizeIcon';
import HappyKingBedIcon from '@stencil/icons/src/HappyKingBedIcon/HappyKingBedIcon';
import HappyLightIcon from '@stencil/icons/src/HappyLightIcon/HappyLightIcon';
import HappySeaViewIcon from '@stencil/icons/src/HappySeaViewIcon/HappySeaViewIcon';

// TYPES
import {RoomTypeColumnProps, RoomProps} from './types';

// HOOKS
import useCarousel from '../../../../../../shared/hooks/useCarousel';

// Omniture Imports
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';

// STYLES
import * as S from './Room.styles';
import {transformCdnImage} from '@stencil/hotels-utils/src/common-utils';

const RoomTypeColumn: React.FC<RoomTypeColumnProps> = ({
  maxNoOfAdults,
  roomSize,
  bedType,
  imageUrl,
  roomTypeName,
  extraBedType,
  roomDescription,
  roomFacilities = [],
  roomImages = [],
  filterHeight = 0,
  reviews = {},
  amenities = [],
  roomCode,
  extraPaxPersuasion,
  roomViewName,
  roomPersuations = {},
  roomHighlights = [],
  bedTypeTNC,
  hotelName,
}) => {
  const multiRoomFlowStatus = useSelector(multiRoomFlowStatusSelector);
  const isAltAcco = useSelector(isAltAccoSelector);
  const isUpSellFlow = true;

  const dispatch = useDispatch();

  const {isModalOpen, closeModal, openModal} = useModal();
  const roomDetailsArr = [
    {
      icon: <HappyRoomSizeIcon fill={'#141823'} height={'24px'} width={'24px'} />,
      text: roomSize,
    },
    {
      icon: <HappyKingBedIcon fill={'#141823'} height={'24px'} width={'24px'} />,
      text: `${bedType} sized bed`,
    },
    {
      icon: <TwoPeopleIcon fill={'#141823'} height={'24px'} width={'24px'} />,
      text: `Max ${maxNoOfAdults} Adults`,
    },
    {
      icon: <HappySeaViewIcon fill={'#141823'} height={'24px'} width={'24px'} />,
      text: roomViewName,
    },
  ];
  /**
   * 🐨 use carousel can be heavily improved improve it
   */
  const {
    dataList,
    increment,
    decrement,
    leftCursor,
    rightCursor,
    isLeftCursorActive,
    isRightCursorActive,
  } = useCarousel(roomImages, 1);

  return (
    <>
      <div id={roomCode + 'upselldd'}></div>
      <S.RoomTypeStyledWrapper width={multiRoomFlowStatus?.hasOccupancyRooms ? '44.2%' : '39.7%'}>
        <S.RoomTypeStickyWrap filterHeight={filterHeight}>
          {roomTypeName && (
            <S.RoomTypeTextStyled filterHeight={filterHeight}>{roomTypeName}</S.RoomTypeTextStyled>
          )}

          {/* replace this with our custom image loader */}
          {imageUrl && (
            <a
              href="#"
              onClick={e => {
                e.preventDefault();

                dispatch(
                  omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
                    cta: {
                      name: `${DetailsPageComponents.Room}_${detailsClick.roomSelection.roomImagesClicked}`,
                      type: omnitureConfig.ctaType.Card,
                      componentName: DetailsPageComponents.Room,
                      destination: '',
                    },
                  }),
                );

                openModal();
              }}
            >
              <S.RoomImageWrapper>
                <S.RoomImageStyled
                  src={transformCdnImage(imageUrl, 428, 230)}
                  alt={`${hotelName} Room Type - ${roomTypeName}`}
                  htmlAttributes={{title: `${hotelName} Room Type - ${roomTypeName}`}}
                />
                {roomImages?.length > 0 ? (
                  <S.RoomImageTag>+{roomImages?.length} Photos</S.RoomImageTag>
                ) : null}
              </S.RoomImageWrapper>
            </a>
          )}

          {/* Only room with jacusi persuation will go here  */}
          {roomPersuations?.desc ? (
            <S.RoomWithJacuzziCardWrapper margin={'0 0 32px'}>
              <HappyLightIcon />
              <span title={roomPersuations?.desc}>{roomPersuations?.desc || ''}</span>
            </S.RoomWithJacuzziCardWrapper>
          ) : null}

          <S.RoomFeaturesWrapper>
            {roomHighlights.slice(0, 4).map(item => {
              if (isAltAcco && item?.identifier === 'GUEST_COUNT') {
                return null;
              }
              return (
                <RoomInfoText alignItems={'center'}>
                  <S.RoomTypeColumnIconsContainer marginTop={'6px'} padding="0 0.5rem 0 0">
                    <img src={item?.iconUrl} width="22px" />
                  </S.RoomTypeColumnIconsContainer>
                  <span
                    style={{
                      paddingLeft: '3px',
                    }}
                  >
                    {item?.text}
                    {bedTypeTNC && item?.identifier === 'BED_TYPE' ? '*' : ''}
                  </span>
                </RoomInfoText>
              );
            })}
          </S.RoomFeaturesWrapper>

          {extraPaxPersuasion && extraPaxPersuasion.length ? (
            <S.ExtraBed_OuterWrapperDiv>
              <ExtraBedRoomTypeInfo
                extraPaxPersuasion={extraPaxPersuasion}
                isUpSellFlow={isUpSellFlow}
              />
            </S.ExtraBed_OuterWrapperDiv>
          ) : null}

          {bedTypeTNC ? <S.BedTypeTNCStyled>*{bedTypeTNC}</S.BedTypeTNCStyled> : ''}

          {reviews?.topReviews?.length > 0 &&
          reviews?.topReviews?.[0]?.reviewContent?.length > 0 ? (
            <S.RoomInfoReview
              onClick={() => {
                dispatch(
                  omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
                    cta: {
                      name: `${DetailsPageComponents.Room}_${detailsClick.roomSelection.viewMoreDetailsModalOpenerClick}`,
                      type: omnitureConfig.ctaType.Button,
                      componentName: DetailsPageComponents.Room,
                      destination: '',
                    },
                  }),
                );

                openModal();
              }}
            >
              <S.ReviewContent>“{reviews?.topReviews?.[0]?.reviewContent}”</S.ReviewContent>
              <span className="textLightGrey font12">
                <span>
                  {reviews?.topReviews?.[0]?.firstName + ' ' + reviews?.topReviews?.[0]?.lastName}
                </span>{' '}
                | <span>{reviews?.topReviews?.[0]?.date}</span>
              </span>
              <S.SingleQuoteWrap>
                <HappySingleQuoteIcon height="2.2rem" />
                <HappySingleQuoteIcon height="2.2rem" />
              </S.SingleQuoteWrap>
            </S.RoomInfoReview>
          ) : null}

          <RoomInfoText>
            {(!!roomFacilities.length || !!roomDescription || !!roomImages.length) && (
              <PrimaryLink
                onClick={() => {
                  dispatch(
                    omnitureThunk(
                      omnitureConfig.pages.DetailsUnif,
                      omnitureConfig.events.ctaClick,
                      {
                        cta: {
                          name: `${DetailsPageComponents.Room}_${detailsClick.roomSelection.viewMoreDetailsModalOpenerClick}`,
                          type: omnitureConfig.ctaType.Hyperlink,
                          componentName: DetailsPageComponents.Room,
                          destination: '',
                        },
                      },
                    ),
                  );

                  openModal();
                }}
                style={{
                  'margin-top': '12px',
                  'font-weight': 'bold',
                }}
              >
                View More Details
              </PrimaryLink>
            )}

            <Modal
              isOpen={isModalOpen}
              closeModal={closeModal}
              modalStyles={{
                height: '90vh',
                width: '84rem',
              }}
              CloseButton={() => null}
            >
              <ViewRoomDetailsChunk
                roomDescription={roomDescription}
                roomFacilities={roomFacilities}
                roomTypeName={roomTypeName}
                roomImages={dataList} // roomImages
                closeModal={closeModal}
                onRightArrowClick={increment}
                onLeftArrowClick={decrement}
                showLeftArrow={isLeftCursorActive}
                showRightArrow={isRightCursorActive}
                leftCursor={leftCursor}
                rightCursor={rightCursor}
                isUpSellFlow={isUpSellFlow}
                roomDetailsArr={roomDetailsArr}
                amenities={amenities}
                extraPaxPersuasion={extraPaxPersuasion}
                reviews={reviews}
                roomHighlights={roomHighlights}
                isAltAcco={isAltAcco}
              />
            </Modal>
          </RoomInfoText>
        </S.RoomTypeStickyWrap>
      </S.RoomTypeStyledWrapper>
    </>
  );
};

const Room: React.FC<RoomProps> = props => {
  const {roomCode = ''} = props.roomTypeColumnProps;
  return (
    <S.RoomContainer id={roomCode} data-testid="detail-roomSelection-room">
      <S.LeftDiv>
        <RoomTypeColumn {...props.roomTypeColumnProps} />
        <RoomFlavors {...props.roomFlavorProps} roomCode={roomCode} />
      </S.LeftDiv>
    </S.RoomContainer>
  );
};

export default Room;
